import { FaceoffDot } from "../../components/rink/FaceoffDot";
import { Menu, type MenuItem } from "../../components/ui/Menu";
import { useI18n } from "../../i18n/I18nProvider";
import type { Device } from "../../lib/api-types";
import { formatDate } from "../api-keys/format-date";
import { formatRelative } from "./format-relative";
import { isOnline } from "./presence";

export interface DeviceRowProps {
  device: Device;
  busy: boolean;
  onUnlink: () => void;
}

/** A linked desktop as a label-grid row. The dot is blue when seen in the last ten minutes. */
export function DeviceRow({ device, busy, onUnlink }: DeviceRowProps) {
  const { t, lang } = useI18n();
  const online = isOnline(device.last_seen_at);
  const items: MenuItem[] = [
    { label: t("devices.unlink"), onSelect: onUnlink, tone: "danger", disabled: busy },
  ];
  return (
    <li className="flex items-start gap-3 py-3">
      <span className="mt-2 flex shrink-0 items-center">
        <FaceoffDot tone={online ? "blue" : "hollow"} />
        <span className="sr-only">{online ? t("devices.online") : t("devices.offline")}</span>
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="truncate text-operate font-semibold text-ink">{device.name}</p>
        <p className="truncate text-note text-ink-2">
          {t("devices.linkedBy", { email: device.linked_by_email })}
        </p>
        <p className="text-note text-ink-3">
          {t("devices.linkedOn", { date: formatDate(device.created_at, lang) })} ·{" "}
          {device.last_seen_at
            ? t("devices.lastSeen", { time: formatRelative(device.last_seen_at, lang) })
            : t("devices.neverSeen")}
        </p>
      </div>
      <Menu label={t("devices.moreActions", { name: device.name })} items={items} />
    </li>
  );
}
